import classNames from 'classnames';
import { useCallback, useRef } from 'react';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { useRevertToPrev } from 'shared/ui/MultiSelect/Hooks/useRevertToPrev';
import type { Option } from 'shared/ui/MultiSelect/interfaces';
import { ClippedText } from 'shared/ui/ClippedText';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';

import style from './ChipButton.module.scss';

export interface SelectButtonProps {
  title: string;
  selectedOption: Option | null;
  setSelected: (options: Option | null) => void;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  leftIcon?: string;
  size?: 'small' | 'big';
  disabled?: boolean;
  isOpen: boolean;
  error?: string;
  withCancel?: boolean;
  isWhite?: boolean;
  isHighlighted?: boolean;
}

export function SelectButton({
  title,
  onFocus,
  leftIcon,
  selectedOption,
  size,
  setSelected,
  onClick,
  error,
  disabled,
  isOpen,
  withCancel,
  isWhite,
  isHighlighted,
}: SelectButtonProps) {
  const parentRef = useRef<HTMLButtonElement | null>(null);

  const hasSelectedValues = Boolean(selectedOption) || isOpen;

  const isSizeSmall = size === 'small';
  const shouldShowLabel = !(isSizeSmall && hasSelectedValues);

  const valuesBig = hasSelectedValues && !isSizeSmall;
  const valuesSmall = hasSelectedValues && isSizeSmall;

  const { updatePrevOptions } = useRevertToPrev<Option>({
    selectedOption,
    setSelected,
  });

  const handleFocus = useCallback(() => {
    updatePrevOptions();
  }, [onFocus]);

  const handleClear = () => setSelected(null);

  return (
    <div
      className={classNames(
        'relative',
        hasSelectedValues && style.hasSelectedValues,
        leftIcon && style.hasLeftIcon,
        error && style.error,
        isSizeSmall && style.small,
        !isSizeSmall && style.big
      )}
    >
      <button
        type="button"
        ref={parentRef}
        className={classNames(style.multiselectBtn, { '!bg-white': isWhite }, isOpen && style.open, {
          '!border-deep-blue-500': isHighlighted,
        })}
        onFocus={handleFocus}
        onClick={onClick}
        disabled={disabled}
      >
        {leftIcon && (
          <span className="mr-2 min-w-[20px]">
            <MaterialIcon className={style.leftIcon}>{leftIcon}</MaterialIcon>
          </span>
        )}
        <span className={classNames(style.chipBlock, valuesBig && 'pt-6', valuesSmall && 'pt-2')}>
          {shouldShowLabel && (
            <span
              className={classNames(style.label, {
                '!text-deep-blue-500': isHighlighted,
              })}
            >
              {title}
            </span>
          )}
          {selectedOption && (
            <ClippedText
              clamp={1}
              text={selectedOption.text}
              className={classNames('w-[80%] text-left', {
                '!text-deep-blue-500': isHighlighted,
              })}
              tooltip={{
                placement: 'bottom-end',
                content: selectedOption.text,
                widthClass: 'max-w-full',
              }}
            />
          )}
        </span>
        {hasSelectedValues && withCancel && (
          <span
            role="button"
            onClick={handleClear}
            className={style.cancelBtn}
            tabIndex={-1}
          >
            <MaterialIcon
              className="text-base"
              {...getDataAutoTestAttributes(['button-close'])}
            >
              cancel
            </MaterialIcon>
          </span>
        )}
        <span className={style.icon}>
          <MaterialIcon className="text-base">expand_more</MaterialIcon>
        </span>
      </button>
    </div>
  );
}
